import React, { useEffect, useState, useRef, memo, Component } from 'react';
import { XCircleIcon, CheckCircleIcon, ChevronDownIcon, LockIcon, ShieldCheckIcon } from 'lucide-react';
// --- Utility Components ---
const FadeInSection = ({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  const [isVisible, setVisible] = useState(false);
  const domRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });
    const currentRef = domRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, []);
  return <div ref={domRef} className={`transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`} data-id="element-1">
      {children}
    </div>;
};
const CTAButton = ({
  className = ''
}: {
  className?: string;
}) => <a href="https://movilidadserena.com/cart/47291812610301:1" target="_blank" rel="noopener noreferrer" className={`w-full group relative inline-flex items-center justify-center px-8 py-5 text-lg md:text-xl font-bold text-white transition-all duration-300 bg-[#C4943A] rounded-xl hover:bg-[#B38634] hover:shadow-lg hover:-translate-y-1 focus:outline-none focus:ring-4 focus:ring-[#C4943A]/50 no-underline ${className}`} aria-label="Descargar mi guía ahora por $19" data-id="element-2">
    <span className="flex items-center text-center" data-id="element-3">
      DESCARGAR MI GUÍA AHORA POR $19
    </span>
  </a>;
const Divider = () => <div className="flex justify-center items-center py-8 opacity-60" aria-hidden="true" data-id="element-4">
    <div className="h-px w-16 bg-[#C4943A]" data-id="element-5"></div>
    <div className="mx-4 text-[#C4943A]" data-id="element-6">✧</div>
    <div className="h-px w-16 bg-[#C4943A]" data-id="element-7"></div>
  </div>;
// --- Main Component ---
export function SalesPage() {
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const heroRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const handleScroll = () => {
      if (heroRef.current) {
        const heroBottom = heroRef.current.getBoundingClientRect().bottom;
        setShowStickyCTA(heroBottom < 0);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const scrollToOffer = () => {
    const offerSection = document.getElementById('offer-section');
    if (offerSection) {
      offerSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const faqs = [{
    q: '¿Necesito equipo especial?',
    a: 'No, solo tu cama y tu propio cuerpo.'
  }, {
    q: '¿Es seguro si tengo artritis?',
    a: 'Sí, los movimientos son de bajo impacto y diseñados para articulaciones sensibles.'
  }, {
    q: '¿Tengo que comprar suplementos caros?',
    a: 'No, la guía se enfoca en alimentos y productos económicos de supermercado.'
  }, {
    q: '¿Puedo leerlo en mi iPad o celular?',
    a: 'Sí, el formato PDF es compatible con cualquier dispositivo.'
  }];
  return <div className="min-h-screen bg-[#FDF8F0] text-[#2D2A26] font-sans selection:bg-[#2A7B72] selection:text-white pb-24 md:pb-0" data-id="element-8">
      {/* 1. HERO SECTION */}
      <header ref={heroRef} className="pt-12 pb-16 px-5 md:pt-20 md:pb-24 bg-[#FDF8F0]" data-id="element-9">
        <div className="max-w-[720px] mx-auto text-center" data-id="element-10">
          <FadeInSection data-id="element-11">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-[#2A7B72]" data-id="element-12">
              Despierta Sin Cadenas: El Método de 5 Minutos para Eliminar la
              Rigidez Articular Matutina
            </h1>
            <h2 className="text-xl md:text-2xl font-medium leading-relaxed mb-10 text-[#2D2A26]/80" data-id="element-13">
              Recupera la libertad de moverte y caminar sin dolor desde el
              primer paso del día, sin fármacos pesados ni ejercicios
              imposibles.
            </h2>

            <div className="relative rounded-2xl overflow-hidden shadow-2xl mb-10 border-4 border-white" data-id="element-14">
              <img src="https://cdn.magicpatterns.com/uploads/x1gGVDePjGHgoiiktCCVZB/heylloyd-image-producto_1.png" alt="Guía digital Despertar Sin Cadenas mostrada en una tableta sobre una mesa de noche junto a una taza de té" className="w-full h-auto object-cover" data-id="element-15" />
            </div>

            <CTAButton data-id="element-16" />
            <p className="mt-4 text-sm text-[#2D2A26]/60 flex items-center justify-center gap-1.5" data-id="element-17">
              <LockIcon className="w-4 h-4" data-id="element-18" /> Acceso instantáneo y seguro
            </p>
          </FadeInSection>
        </div>
      </header>

      {/* 2. OPENING HOOK */}
      <section className="py-16 px-5 bg-[#FAF5EB]" data-id="element-19">
        <div className="max-w-[720px] mx-auto" data-id="element-20">
          <FadeInSection data-id="element-21">
            <div className="text-lg md:text-xl leading-[1.8] italic text-[#2D2A26]/90 border-l-4 border-[#C4943A] pl-6 md:pl-8 py-2" data-id="element-22">
              <p data-id="element-23">
                Son las 6:30 de la mañana. Abres los ojos y, antes de que puedas
                disfrutar del nuevo día, sientes esa vieja y familiar sensación.
                Tus rodillas pesan como el plomo, tus dedos están trabados y tu
                espalda baja se siente como una tabla de madera.
              </p>
              <p className="mt-4" data-id="element-24">
                Intentas bajar los pies de la cama, pero el primer pinchazo de
                dolor te detiene. No es solo "la edad". Es tu cuerpo pidiendo a
                gritos la lubricación que perdió durante la noche.
              </p>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* 3. THE PROBLEM */}
      <section className="py-16 px-5 bg-[#FDF8F0]" data-id="element-25">
        <div className="max-w-[720px] mx-auto" data-id="element-26">
          <FadeInSection data-id="element-27">
            <p className="text-lg md:text-xl leading-[1.8] mb-6" data-id="element-28">
              La mayoría de las mujeres de más de 65 años en Estados Unidos
              aceptan vivir con este dolor porque les han dicho que es "normal".
              Se resignan a caminar con cuidado, a dejar de jugar con sus nietos
              o a evitar las escaleras.
            </p>
            <p className="text-lg md:text-xl leading-[1.8]" data-id="element-29">
              Has probado cremas que solo huelen fuerte, pastillas que te
              irritan el estómago y consejos de "expertos" que nunca han sentido
              lo que es tener las articulaciones oxidadas al despertar. Nada de
              eso funciona porque no ataca la raíz:{' '}
              <strong data-id="element-30">
                la viscosidad del líquido que debería proteger tus huesos.
              </strong>
            </p>
          </FadeInSection>
        </div>
      </section>

      {/* 4. WHY WHAT YOU TRIED DIDN'T WORK */}
      <section className="py-16 px-5 bg-white" data-id="element-31">
        <div className="max-w-[720px] mx-auto" data-id="element-32">
          <FadeInSection data-id="element-33">
            <h3 className="text-3xl md:text-4xl font-bold mb-10 text-center text-[#2D2A26]" data-id="element-34">
              Por qué lo que has intentado no funciona
            </h3>
            <ul className="space-y-6" data-id="element-35">
              {['Los fármacos solo "tapan" el dolor por unas horas, pero no lubrican la articulación.', 'Los ejercicios genéricos son demasiado intensos y terminan inflamándote más.', 'Las rodilleras y soportes debilitan tus músculos si los usas todo el día.', 'Ignorar el dolor solo hace que la rigidez se vuelva permanente.'].map((item, idx) => <li key={idx} className="flex items-start gap-4 p-4 rounded-xl bg-[#FAF5EB]/50" data-id="element-36">
                  <XCircleIcon className="w-7 h-7 text-red-600 shrink-0 mt-1" aria-hidden="true" data-id="element-37" />
                  <span className="text-lg md:text-xl leading-relaxed" data-id="element-38">
                    {item}
                  </span>
                </li>)}
            </ul>
          </FadeInSection>
        </div>
      </section>

      {/* 5. THE UNIQUE MECHANISM */}
      <section className="py-20 px-5 bg-[#2A7B72] text-white my-8" data-id="element-39">
        <div className="max-w-[720px] mx-auto text-center" data-id="element-40">
          <FadeInSection data-id="element-41">
            <h3 className="text-3xl md:text-4xl font-bold mb-8 text-[#FDF8F0]" data-id="element-42">
              Presentamos el método de <br className="hidden md:block" data-id="element-43" />
              <span className="text-[#C4943A]" data-id="element-44">
                Micro-Movimiento Termo-Activado
              </span>
              .
            </h3>
            <p className="text-lg md:text-xl leading-[1.8] text-white/90" data-id="element-45">
              A diferencia de cualquier rutina de gimnasio, este sistema utiliza
              el calor natural de tu cuerpo bajo las sábanas para "derretir" la
              rigidez antes de que tus pies toquen el suelo. Son movimientos tan
              sutiles que parecen invisibles, pero envían una señal inmediata a
              tus articulaciones para que produzcan su propio lubricante
              natural.
            </p>
          </FadeInSection>
        </div>
      </section>

      {/* 6. WHAT YOU GET */}
      <section className="py-16 px-5 bg-[#FDF8F0]" data-id="element-46">
        <div className="max-w-[720px] mx-auto" data-id="element-47">
          <FadeInSection data-id="element-48">
            <h3 className="text-3xl md:text-4xl font-bold mb-10 text-center text-[#2D2A26]" data-id="element-49">
              Lo que recibes hoy
            </h3>
            <div className="space-y-6 mb-12" data-id="element-50">
              {[{
              title: 'La Guía Digital Completa',
              desc: '"Despertar Sin Cadenas" (Formato PDF).'
            }, {
              title: 'La Rutina de 5 Minutos',
              desc: 'Paso a Paso para hacer antes de salir de la cama.'
            }, {
              title: 'Lista de Compras Antiinflamatoria',
              desc: 'Los 3 aliados que encuentras en cualquier Walmart o CVS.'
            }, {
              title: 'El Protocolo de Rescate Térmico',
              desc: 'Cuándo usar frío y cuándo usar calor.'
            }, {
              title: 'Guía de Suplementos Esenciales',
              desc: 'Específicos para el mercado de USA.'
            }].map((item, idx) => <div key={idx} className="flex items-start gap-4 p-5 rounded-xl bg-white shadow-sm border border-[#C4943A]/20" data-id="element-51">
                  <CheckCircleIcon className="w-8 h-8 text-[#2A7B72] shrink-0 mt-0.5" aria-hidden="true" data-id="element-52" />
                  <div data-id="element-53">
                    <h4 className="text-xl font-bold text-[#2D2A26] mb-1" data-id="element-54">
                      {item.title}
                    </h4>
                    <p className="text-lg text-[#2D2A26]/80" data-id="element-55">{item.desc}</p>
                  </div>
                </div>)}
            </div>

            <div className="text-center" data-id="element-56">
              <CTAButton data-id="element-57" />
            </div>
          </FadeInSection>
        </div>
      </section>

      <Divider data-id="element-58" />

      {/* 7. WHO THIS IS FOR / NOT FOR */}
      <section className="py-16 px-5 bg-[#FAF5EB]" data-id="element-59">
        <div className="max-w-[800px] mx-auto" data-id="element-60">
          <FadeInSection data-id="element-61">
            <div className="grid md:grid-cols-2 gap-8 md:gap-12" data-id="element-62">
              {/* For You */}
              <div className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border-t-4 border-[#2A7B72]" data-id="element-63">
                <h4 className="text-2xl font-bold mb-6 text-[#2A7B72]" data-id="element-64">
                  Para ti si...
                </h4>
                <ul className="space-y-5" data-id="element-65">
                  {['Mujeres de 65 años o más que sienten su cuerpo "trabado" al despertar.', 'Personas que viven en USA y quieren soluciones prácticas que encuentren en su farmacia local.', 'Quienes buscan una solución natural para reducir el uso de analgésicos.', 'Abuelas activas que no quieren que el dolor les impida seguir su ritmo de vida.'].map((item, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-66">
                      <CheckCircleIcon className="w-6 h-6 text-[#2A7B72] shrink-0 mt-1" aria-hidden="true" data-id="element-67" />
                      <span className="text-lg leading-relaxed" data-id="element-68">{item}</span>
                    </li>)}
                </ul>
              </div>

              {/* Not For You */}
              <div className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border-t-4 border-red-600" data-id="element-69">
                <h4 className="text-2xl font-bold mb-6 text-red-700" data-id="element-70">
                  No es para ti si...
                </h4>
                <ul className="space-y-5" data-id="element-71">
                  {['Personas que buscan una "cura milagrosa" de una noche sin hacer ningún cambio.', 'Quienes tienen una lesión quirúrgica reciente que requiere atención médica inmediata.', 'Personas que no están dispuestas a dedicar 5 minutos a su bienestar matutino.'].map((item, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-72">
                      <XCircleIcon className="w-6 h-6 text-red-600 shrink-0 mt-1" aria-hidden="true" data-id="element-73" />
                      <span className="text-lg leading-relaxed" data-id="element-74">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* 8. HOW IT WORKS */}
      <section className="py-20 px-5 bg-white" data-id="element-75">
        <div className="max-w-[720px] mx-auto" data-id="element-76">
          <FadeInSection data-id="element-77">
            <h3 className="text-3xl md:text-4xl font-bold mb-12 text-center text-[#2D2A26]" data-id="element-78">
              ¿Cómo funciona?
            </h3>
            <div className="space-y-8" data-id="element-79">
              {['Descarga la guía PDF inmediatamente en tu teléfono, tableta o computadora.', 'Lee el Capítulo 2 esta noche para memorizar los 5 micro-movimientos.', 'Mañana, antes de levantarte, aplica el método y siente la diferencia en tu primer paso.'].map((step, idx) => <div key={idx} className="flex items-center gap-6 p-6 rounded-2xl bg-[#FDF8F0]" data-id="element-80">
                  <div className="w-16 h-16 rounded-full bg-[#2A7B72] text-white flex items-center justify-center text-2xl font-bold shrink-0 font-serif shadow-md" data-id="element-81">
                    {idx + 1}
                  </div>
                  <p className="text-lg md:text-xl leading-relaxed font-medium" data-id="element-82">
                    {step}
                  </p>
                </div>)}
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* 9. PROOF / BELIEVABILITY */}
      <section className="py-16 px-5 bg-[#FAF5EB]" data-id="element-83">
        <div className="max-w-[720px] mx-auto" data-id="element-84">
          <FadeInSection data-id="element-85">
            <div className="bg-white p-8 md:p-10 rounded-3xl shadow-lg border border-[#C4943A]/30 relative overflow-hidden" data-id="element-86">
              <div className="absolute top-0 left-0 w-2 h-full bg-[#C4943A]" data-id="element-87"></div>
              <h3 className="text-3xl font-bold mb-6 text-[#2D2A26]" data-id="element-88">
                ¿Qué cambia en 7 días?
              </h3>
              <p className="text-lg md:text-xl leading-[1.8] mb-6" data-id="element-89">
                <strong data-id="element-90">Al cuarto día</strong>, notarás que el tiempo que tardas
                en "calentar" tu cuerpo se reduce a la mitad.
              </p>
              <p className="text-lg md:text-xl leading-[1.8]" data-id="element-91">
                <strong data-id="element-92">Para el séptimo día</strong>, la ansiedad de no saber
                cómo responderán tus rodillas al bajar de la cama habrá
                desaparecido, reemplazada por una confianza real en tu propio
                movimiento.
              </p>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* 10. FAQ */}
      <section className="py-20 px-5 bg-[#FDF8F0]" data-id="element-93">
        <div className="max-w-[720px] mx-auto" data-id="element-94">
          <FadeInSection data-id="element-95">
            <h3 className="text-3xl md:text-4xl font-bold mb-10 text-center text-[#2D2A26]" data-id="element-96">
              Preguntas Frecuentes
            </h3>
            <div className="space-y-4" data-id="element-97">
              {faqs.map((faq, idx) => <div key={idx} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden transition-all duration-300" data-id="element-98">
                  <button onClick={() => toggleFaq(idx)} className="w-full flex items-center justify-between p-6 text-left focus:outline-none focus:bg-[#FAF5EB]" aria-expanded={openFaqIndex === idx} data-id="element-99">
                    <span className="text-lg md:text-xl font-bold text-[#2D2A26] pr-4" data-id="element-100">
                      {faq.q}
                    </span>
                    <ChevronDownIcon className={`w-6 h-6 text-[#C4943A] transition-transform duration-300 shrink-0 ${openFaqIndex === idx ? 'rotate-180' : ''}`} data-id="element-101" />
                  </button>
                  <div className={`transition-all duration-300 ease-in-out ${openFaqIndex === idx ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-102">
                    <div className="p-6 pt-0 text-lg text-[#2D2A26]/80 leading-relaxed" data-id="element-103">
                      {faq.a}
                    </div>
                  </div>
                </div>)}
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* 11. OFFER + PRICE BLOCK & 12. FINAL CTA */}
      <section id="offer-section" className="py-20 px-5 bg-white border-t-8 border-[#2A7B72]" data-id="element-104">
        <div className="max-w-[600px] mx-auto text-center" data-id="element-105">
          <FadeInSection data-id="element-106">
            <h3 className="text-3xl md:text-4xl font-bold mb-4 text-[#2D2A26]" data-id="element-107">
              Comienza tu mañana sin dolor
            </h3>
            <p className="text-xl text-[#2D2A26]/80 mb-8" data-id="element-108">
              Obtén acceso completo a "Despertar Sin Cadenas" hoy.
            </p>

            <div className="bg-[#FAF5EB] rounded-3xl p-8 md:p-12 shadow-xl border border-[#C4943A]/20 mb-8 relative" data-id="element-109">
              <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-[#C4943A] text-white px-6 py-2 rounded-full text-sm font-bold tracking-wider uppercase shadow-md" data-id="element-110">
                Oferta Especial
              </div>

              <div className="flex justify-center items-center gap-4 mb-4 mt-2" data-id="element-111">
                <span className="text-3xl text-gray-400 line-through font-serif" data-id="element-112">
                  $49
                </span>
                <span className="text-6xl md:text-7xl font-bold text-[#2A7B72] font-serif" data-id="element-113">
                  $19
                </span>
              </div>

              <p className="text-lg text-[#2D2A26]/70 mb-8 italic" data-id="element-114">
                Menos de lo que cuesta un frasco de cremas para el dolor que no
                funcionan.
              </p>

              <CTAButton data-id="element-115" />

              <div className="mt-6 flex flex-col items-center justify-center gap-2 text-sm text-[#2D2A26]/60" data-id="element-116">
                <div className="flex items-center gap-2" data-id="element-117">
                  <ShieldCheckIcon className="w-5 h-5 text-green-600" data-id="element-118" />
                  <span data-id="element-119">Pago 100% seguro y encriptado</span>
                </div>
                <p data-id="element-120">*Acceso instantáneo después de tu compra segura.*</p>
              </div>
            </div>

            <img src="https://cdn.magicpatterns.com/uploads/x1gGVDePjGHgoiiktCCVZB/heylloyd-image-producto_1.png" alt="Guía digital Despertar Sin Cadenas" className="w-full max-w-[300px] mx-auto rounded-xl shadow-lg opacity-90" data-id="element-121" />
          </FadeInSection>
        </div>
      </section>

      {/* STICKY MOBILE CTA */}
      <div className={`fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 shadow-[0_-10px_20px_rgba(0,0,0,0.05)] z-50 transition-transform duration-500 md:hidden ${showStickyCTA ? 'translate-y-0' : 'translate-y-full'}`} data-id="element-122">
        <a href="https://movilidadserena.com/cart/47291812610301:1" target="_blank" rel="noopener noreferrer" className="w-full flex items-center justify-center px-6 py-4 text-lg font-bold text-white bg-[#C4943A] rounded-xl active:bg-[#B38634] shadow-lg no-underline" data-id="element-123">
          <LockIcon className="w-5 h-5 mr-2" data-id="element-124" />
          DESCARGAR POR $19
        </a>
      </div>
    </div>;
}